import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createDeal } from '../api';
import ImageUpload from '../components/ImageUpload';
import './CreateDealPage.css';

const MEAL_TYPES = ['all', 'Dine-in', 'Takeout', 'Delivery'];

const TERMS_PRESETS = [
  'Valid for dine-in only. One per customer. Not valid with other offers.',
  'Valid for dine-in and takeout. One redemption per customer per visit.',
  'Dine-in only. Not valid on statutory holidays. One per table.',
  'Valid any day of the week. One redemption per customer.',
  'Valid Monday–Friday only. Not valid on weekends or holidays.',
  'Valid weekends only (Saturday & Sunday). One per table.',
  'Reservation recommended. One redemption per table, regardless of party size.',
  'Custom — write your own terms',
];

const inDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
};
const tomorrow = () => inDays(1);

export default function CreateDealPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [termsPreset, setTermsPreset] = useState(TERMS_PRESETS[0]);
  const [form, setForm] = useState({
    title: '', description: '', what_you_get: '',
    meal_type: 'Dine-in',
    original_price: '', deal_price: '',
    max_redemptions: '',
    valid_until: inDays(30),
    terms: TERMS_PRESETS[0],
    image_url: ''
  });

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleTermsPreset = (val) => {
    setTermsPreset(val);
    if (val !== 'Custom — write your own terms') {
      setF('terms', val);
    } else {
      setF('terms', '');
    }
  };

  const discount = form.original_price && form.deal_price
    ? Math.round(((form.original_price - form.deal_price) / form.original_price) * 100)
    : 0;
  const savings = form.original_price && form.deal_price
    ? (Number(form.original_price) - Number(form.deal_price)).toFixed(2)
    : '0.00';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Number(form.deal_price) >= Number(form.original_price)) {
      return setError('Deal price must be lower than original price');
    }
    if (discount < 5) return setError('Discount must be at least 5%');
    setLoading(true); setError('');
    try {
      await createDeal({
        ...form,
        original_price: Number(form.original_price),
        deal_price: Number(form.deal_price),
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        valid_until: new Date(form.valid_until).toISOString(),
      });
      navigate('/business/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to create deal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-deal-page">
      <div className="container">
        <Link to="/business/dashboard" className="back-link">← Dashboard</Link>
        <div className="create-deal-grid">

          <div className="create-deal-form">
            <h1>Create a new deal</h1>
            <p className="create-deal-sub">Your deal will be live on halfprice.ca as soon as you submit.</p>
            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3 className="form-section-title">Deal basics</h3>
                <div className="form-group">
                  <label>Deal title *</label>
                  <input className="form-control" placeholder="e.g. Butter Chicken Feast for 2" value={form.title} onChange={e => setF('title', e.target.value)} required maxLength={120} />
                  <span className="form-hint">{form.title.length}/120 characters</span>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea className="form-control" rows={2} placeholder="Describe the dish or experience..." value={form.description} onChange={e => setF('description', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>What's included</label>
                  <textarea className="form-control" rows={2} placeholder="e.g. 2 mains · 4 garlic naan · 2 drinks" value={form.what_you_get} onChange={e => setF('what_you_get', e.target.value)} />
                  <span className="form-hint">Listed items build trust — be specific.</span>
                </div>
                <div className="form-group">
                  <label>Meal type</label>
                  <select className="form-control" value={form.meal_type} onChange={e => setF('meal_type', e.target.value)}>
                    {MEAL_TYPES.map(m => <option key={m} value={m}>{m === 'all' ? 'All types' : m}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Pricing</h3>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Original price ($) *</label>
                    <input type="number" className="form-control" placeholder="0.00" min="1" step="0.01" value={form.original_price} onChange={e => setF('original_price', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Deal price ($) *</label>
                    <input type="number" className="form-control" placeholder="0.00" min="0.01" step="0.01" value={form.deal_price} onChange={e => setF('deal_price', e.target.value)} required />
                  </div>
                </div>
                {discount > 0 && (
                  <div className="discount-preview">
                    <span className="dp-chip">{discount}% discount</span>
                    <span className="dp-text">Customers save <strong>${savings}</strong></span>
                    {discount >= 40 && <span className="dp-great">Great deal! 🔥</span>}
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Availability</h3>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Valid until *</label>
                    <input type="date" className="form-control" min={tomorrow()} value={form.valid_until} onChange={e => setF('valid_until', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Max redemptions (optional)</label>
                    <input type="number" className="form-control" placeholder="Leave blank for unlimited" min="1" value={form.max_redemptions} onChange={e => setF('max_redemptions', e.target.value)} />
                  </div>
                </div>
                <div className="quick-dates">
                  <span className="quick-dates-label">Quick set:</span>
                  {[7, 14, 30, 60, 90].map(n => (
                    <button key={n} type="button" className="quick-date-btn" onClick={() => setF('valid_until', inDays(n))}>
                      {n} days
                    </button>
                  ))}
                </div>
                <p className="form-hint" style={{marginTop:8}}>
                  Tip: longer expiry (60-90 days) means less re-listing work for you.
                </p>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Terms & conditions</h3>
                <div className="form-group">
                  <label>Choose a template</label>
                  <select className="form-control" value={termsPreset} onChange={e => handleTermsPreset(e.target.value)}>
                    {TERMS_PRESETS.map(t => <option key={t} value={t}>{t.length > 70 ? t.slice(0,70)+'…' : t}</option>)}
                  </select>
                </div>
                {termsPreset === 'Custom — write your own terms' && (
                  <div className="form-group">
                    <label>Your custom terms</label>
                    <textarea className="form-control" rows={2} placeholder="Write your own terms and conditions..." value={form.terms} onChange={e => setF('terms', e.target.value)} />
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Photo</h3>
                <ImageUpload value={form.image_url} onChange={(url) => setF('image_url', url)} />
              </div>

              <div className="create-deal-actions">
                <Link to="/business/dashboard" className="btn btn-ghost">Cancel</Link>
                <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
                  {loading ? 'Publishing…' : 'Publish deal →'}
                </button>
              </div>
            </form>
          </div>

          <div className="deal-preview-panel">
            <h3>Live preview</h3>
            <p className="preview-sub">This is how your deal will appear to diners</p>
            <div className="preview-card">
              <div className="preview-img">
                {form.image_url
                  ? <img src={form.image_url} alt="" onError={e => { e.target.style.display='none'; }} />
                  : <span style={{ fontSize: 48 }}>🍽</span>
                }
                {discount > 0 && <div className="preview-badge">{discount}% off</div>}
              </div>
              <div className="preview-body">
                <div className="preview-biz">Your restaurant · Winnipeg</div>
                <div className="preview-title">{form.title || 'Your deal title will appear here'}</div>
                <div className="preview-price-row">
                  {form.deal_price && <span className="preview-price">${Number(form.deal_price).toFixed(2)}</span>}
                  {form.original_price && <span className="preview-orig">${Number(form.original_price).toFixed(2)}</span>}
                </div>
                <div className="preview-footer">
                  <span>{form.meal_type !== 'all' ? form.meal_type : 'All types'}</span>
                  <span>{form.valid_until ? `${Math.ceil((new Date(form.valid_until) - new Date()) / 86400000)} days left` : '—'}</span>
                </div>
              </div>
            </div>

            <div className="preview-tips">
              <h4>Tips for a great deal</h4>
              <ul>
                <li>Aim for 40–50% discount — these get far more clicks</li>
                <li>Be specific in "What's included"</li>
                <li>Add a photo — deals with images get 3× more views</li>
                <li>Set expiry to 30-60 days to reduce re-listing</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
